import { useNavigate } from 'react-router-dom'
import { useState } from 'react'
import {
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  ExternalLink,
  Loader2,
  Mail,
  Phone,
  Search,
  User,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import {
  ENRICHMENT_STAGES,
  OUTREACH_STAGES,
  STAGE_CONFIG,
  PipelineStage,
} from '@/components/pipeline-stage-badge'
import type { Contact } from '@/services/api'

interface QualifiedData {
  score?: number
  qualification_reasons?: string[] | Record<string, unknown>
  contact_enrichment_status?: string
}

export interface CompanyPipelineStepperCompany {
  id: string
  name: string
  source: string | null
  stage: PipelineStage
  created_at: string
  qualified?: QualifiedData | null
  hasAnalysis?: boolean
  contacts?: Contact[]
  phone?: string | null
  company_email?: string | null
  company_emails?: string[]
  outreach?: {
    email_variables?: {
      personalizedIntro?: string
      specificBenefit?: string
      callToAction?: string
      companyName?: string
    }
    sent?: boolean
  } | null
  latest_call?: {
    id: string
    status:
      | 'queued'
      | 'dialing'
      | 'in_progress'
      | 'completed'
      | 'failed'
      | 'no_answer'
      | 'busy'
      | 'cancelled'
    booking_status: 'not_attempted' | 'slots_offered' | 'booked' | 'failed' | 'declined'
    booked_start_at?: string | null
    booked_end_at?: string | null
    booked_timezone?: string | null
  } | null
  calendar_ready_for_user?: boolean
  profile_summary?: string | null
  profile_freshness?: {
    last_profiled_at?: string | null
    is_stale?: boolean
  } | null
  industry_tags?: string[]
  offering_tags?: string[]
  company_type_tags?: string[]
  industry?: string | null
  website?: string | null
  activities?: Array<{
    id: string
    activity_type: string
    channel?: string
    target_value?: string
    created_at: string
  }>
}

export interface CompanyPipelineStepperOutreachPreview {
  resolved_contact: {
    email: string | null
    phone: string | null
    name: string | null
  }
}

interface CompanyPipelineStepperProps {
  company: CompanyPipelineStepperCompany
  outreachPreview?: CompanyPipelineStepperOutreachPreview | null
  onQualify: () => void
  onAnalyze: () => void
  onFindContact: () => void
  onGenerateOutreach: () => void
  onSendEmail: () => void
  onCall: () => void
  isPipelinePending: boolean
  isAnalyzePending?: boolean
  isQualifyPending: boolean
  isSendEmailPending: boolean
  isCallPending: boolean
}

const SUMMARY_PREVIEW_LENGTH = 180

function AnalyzedSummary({ summary }: { summary: string }) {
  const [expanded, setExpanded] = useState(false)
  const isLong = summary.length > SUMMARY_PREVIEW_LENGTH
  const displayText = isLong && !expanded
    ? `${summary.slice(0, SUMMARY_PREVIEW_LENGTH).trim()}…`
    : summary

  return (
    <div className="space-y-1">
      <p className="text-xs text-muted-foreground whitespace-pre-wrap">
        {displayText}
      </p>
      {isLong && (
        <button
          type="button"
          onClick={() => setExpanded((e) => !e)}
          className="text-xs text-primary hover:underline inline-flex items-center gap-0.5"
        >
          {expanded ? (
            <>
              <ChevronUp className="h-3 w-3" />
              Show less
            </>
          ) : (
            <>
              <ChevronDown className="h-3 w-3" />
              Show more
            </>
          )}
        </button>
      )}
    </div>
  )
}

function getPrimaryContact(contacts: Contact[] | undefined): Contact | null {
  if (!contacts?.length) return null
  const primary = contacts.find((c) => c.is_primary)
  return primary ?? contacts[0] ?? null
}

function hasContactEmail(
  company: CompanyPipelineStepperCompany,
  preview?: CompanyPipelineStepperOutreachPreview | null
): boolean {
  const contact = getPrimaryContact(company.contacts)
  if (contact?.email) return true
  if (preview?.resolved_contact?.email) return true
  const emails = company.contacts?.flatMap((c) => (c.email ? [c.email] : [])) ?? []
  return emails.length > 0
}

function hasEmailSent(company: CompanyPipelineStepperCompany): boolean {
  return company.outreach?.sent ?? false
}

function hasPhoneCalled(company: CompanyPipelineStepperCompany): boolean {
  if (
    company.latest_call?.status === 'completed' ||
    company.latest_call?.booking_status === 'booked'
  ) {
    return true
  }
  return (
    company.activities?.some((a) => a.activity_type === 'phone_called') ?? false
  )
}

function isCallInFlight(company: CompanyPipelineStepperCompany): boolean {
  return (
    company.latest_call?.status === 'queued' ||
    company.latest_call?.status === 'dialing' ||
    company.latest_call?.status === 'in_progress'
  )
}

interface EmailInfo {
  email: string
  source: 'company' | 'contact'
  contactName?: string
}

interface PhoneInfo {
  phone: string
  source: 'company' | 'contact'
  contactName?: string
}

function getAllEmails(company: CompanyPipelineStepperCompany): EmailInfo[] {
  const emails: EmailInfo[] = []
  const seen = new Set<string>()

  // Company emails first
  const companyEmails = company.company_emails?.length
    ? company.company_emails
    : company.company_email
      ? [company.company_email]
      : []

  for (const email of companyEmails) {
    if (email && !seen.has(email.toLowerCase())) {
      seen.add(email.toLowerCase())
      emails.push({ email, source: 'company' })
    }
  }

  // Contact emails
  for (const contact of company.contacts ?? []) {
    if (contact.email && !seen.has(contact.email.toLowerCase())) {
      seen.add(contact.email.toLowerCase())
      emails.push({
        email: contact.email,
        source: 'contact',
        contactName: contact.name || undefined,
      })
    }
  }

  return emails
}

function getAllPhones(company: CompanyPipelineStepperCompany): PhoneInfo[] {
  const phones: PhoneInfo[] = []
  const seen = new Set<string>()

  // Normalize phone for comparison (remove spaces, dashes, etc.)
  const normalize = (p: string) => p.replace(/[\s\-().+]/g, '')

  // Company phone first
  if (company.phone) {
    const norm = normalize(company.phone)
    if (!seen.has(norm)) {
      seen.add(norm)
      phones.push({ phone: company.phone, source: 'company' })
    }
  }

  // Contact phones
  for (const contact of company.contacts ?? []) {
    if (contact.phone) {
      const norm = normalize(contact.phone)
      if (!seen.has(norm)) {
        seen.add(norm)
        phones.push({
          phone: contact.phone,
          source: 'contact',
          contactName: contact.name || undefined,
        })
      }
    }
  }

  return phones
}

function ContactStepContent({
  company,
  resolvedContact,
  hasContact,
  onFindContact,
  isPipelinePending,
}: {
  company: CompanyPipelineStepperCompany
  resolvedContact?: { email: string | null; phone: string | null; name: string | null }
  hasContact: boolean
  onFindContact: () => void
  isPipelinePending: boolean
}) {
  const allEmails = getAllEmails(company)
  const allPhones = getAllPhones(company)
  const hasExistingContacts = allEmails.length > 0 || allPhones.length > 0

  return (
    <div className="space-y-3">
      {/* Existing emails section */}
      {allEmails.length > 0 && (
        <div className="space-y-1.5">
          <p className="text-xs font-medium tracking-wide text-muted-foreground">
            Emails ({allEmails.length})
          </p>
          <div className="space-y-1">
            {allEmails.map(({ email, source, contactName }) => (
              <div
                key={email}
                className="flex items-center gap-2 text-sm"
              >
                <Mail className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                <span className="truncate">{email}</span>
                <span className="shrink-0 rounded bg-muted px-1.5 py-0.5 text-xs text-muted-foreground">
                  {source === 'company'
                    ? 'company'
                    : contactName
                      ? contactName
                      : 'contact'}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Existing phones section */}
      {allPhones.length > 0 && (
        <div className="space-y-1.5">
          <p className="text-xs font-medium tracking-wide text-muted-foreground">
            Phone numbers ({allPhones.length})
          </p>
          <div className="space-y-1">
            {allPhones.map(({ phone, source, contactName }) => (
              <div
                key={phone}
                className="flex items-center gap-2 text-sm"
              >
                <Phone className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                <span>{phone}</span>
                <span className="shrink-0 rounded bg-muted px-1.5 py-0.5 text-xs text-muted-foreground">
                  {source === 'company'
                    ? 'company'
                    : contactName
                      ? contactName
                      : 'contact'}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* No contacts found */}
      {!hasExistingContacts && !hasContact && (
        <p className="text-sm text-muted-foreground">
          No contact information found yet
        </p>
      )}

      {/* Action buttons */}
      <div className="flex flex-wrap items-center gap-2 pt-1">
        <Button
          size="sm"
          variant="outline"
          onClick={onFindContact}
          disabled={isPipelinePending}
        >
          {isPipelinePending ? (
            <Loader2 className="mr-2 h-3 w-3 animate-spin" />
          ) : (
            <Search className="mr-2 h-3 w-3" />
          )}
          {hasExistingContacts ? 'Find more contacts' : 'Find contact'}
        </Button>
        <Button
          size="sm"
          variant="ghost"
          className="text-muted-foreground"
          disabled
        >
          Search online (coming soon)
        </Button>
      </div>
    </div>
  )
}

export function CompanyPipelineStepper({
  company,
  outreachPreview,
  onQualify,
  onAnalyze,
  onFindContact,
  onGenerateOutreach,
  onSendEmail,
  onCall,
  isPipelinePending,
  isAnalyzePending = false,
  isQualifyPending,
  isSendEmailPending,
  isCallPending,
}: CompanyPipelineStepperProps) {
  const navigate = useNavigate()
  const currentOrder = STAGE_CONFIG[company.stage].order
  const hasContact = company.qualified?.contact_enrichment_status === 'found'
  const hasEmail = hasContactEmail(company, outreachPreview)
  const emailSent = hasEmailSent(company)
  const phoneCalled = hasPhoneCalled(company)
  const callInFlight = isCallInFlight(company)
  const resolvedContact = outreachPreview?.resolved_contact

  const allStages = [...ENRICHMENT_STAGES, ...OUTREACH_STAGES]
  const enrichmentCount = ENRICHMENT_STAGES.length

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Pipeline Progress</CardTitle>
        <p className="text-sm text-muted-foreground">
          Enrich companies with qualification, website analysis, and contact information before generating outreach
        </p>
      </CardHeader>
      <CardContent>
        <div className="relative">
          {allStages.map((stageKey, index) => {
            const order = index + 1
            const isCompleted = order < currentOrder
            const isCurrent = order === currentOrder
            const config = STAGE_CONFIG[stageKey]
            const Icon = config.icon
            const isFirstOutreach = index === enrichmentCount

            return (
              <div key={stageKey}>
                {/* Group header for Outreach section */}
                {isFirstOutreach && (
                  <div className="mb-4 mt-2 flex items-center gap-3">
                    <div className="h-px flex-1 bg-border" />
                    <span className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                      Outreach
                    </span>
                    <div className="h-px flex-1 bg-border" />
                  </div>
                )}
                {/* Group header for Enrichment section (first item only) */}
                {index === 0 && (
                  <div className="mb-4 flex items-center gap-3">
                    <span className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                      Enrichment
                    </span>
                    <div className="h-px flex-1 bg-border" />
                  </div>
                )}
                <div className="flex gap-4 pb-8 last:pb-0">
                  {/* Circle + vertical line */}
                  <div className="relative flex flex-col items-center">
                    <div
                      className={cn(
                        'flex h-9 w-9 shrink-0 items-center justify-center rounded-full border-2 transition-colors',
                        isCompleted && 'border-primary bg-primary text-primary-foreground',
                        isCurrent && 'border-primary bg-primary/10 text-primary shadow-sm',
                        !isCompleted && !isCurrent && 'border-border bg-muted/40 text-muted-foreground'
                      )}
                      title={config.tooltip}
                    >
                      {isCompleted ? (
                        <CheckCircle2 className="h-4 w-4" />
                      ) : (
                        <Icon className="h-4 w-4" />
                      )}
                    </div>
                    {index < allStages.length - 1 && (
                      <div
                        className={cn(
                          'mt-2 w-0.5 flex-1 min-h-[48px] rounded-full',
                          isCompleted ? 'bg-primary' : 'bg-border'
                        )}
                      />
                    )}
                  </div>

                  <div className="min-w-0 flex-1 space-y-1 pt-0.5">
                      <p
                        className={cn(
                          'text-sm font-medium',
                          isCompleted && 'text-muted-foreground',
                          isCurrent && 'text-card-foreground',
                          !isCompleted && !isCurrent && 'text-muted-foreground'
                        )}
                      >
                        {order}. {config.label}
                      </p>

                      {/* Step-specific content */}
                      {stageKey === 'sourced' && (
                        <p className="text-sm text-muted-foreground">
                          Sourced from {company.source || 'unknown'}
                          {company.created_at && (
                            <span className="ml-1">
                              · {new Date(company.created_at).toLocaleDateString()}
                            </span>
                          )}
                        </p>
                      )}

                      {stageKey === 'qualified' && (
                        <>
                          {company.qualified ? (
                            <div className="space-y-1.5">
                              <p className="text-sm text-muted-foreground">
                                Score: {company.qualified.score ?? '—'}
                                {Array.isArray(company.qualified.qualification_reasons) &&
                                  company.qualified.qualification_reasons.length > 0 && (
                                    <span className="ml-1">
                                      · {company.qualified.qualification_reasons.length} reason(s)
                                    </span>
                                  )}
                              </p>
                              {Array.isArray(company.qualified.qualification_reasons) &&
                                company.qualified.qualification_reasons.length > 0 && (
                                  <ul className="text-xs text-muted-foreground list-disc list-inside space-y-0.5">
                                    {company.qualified.qualification_reasons.map((reason, idx) => (
                                      <li key={idx}>{reason}</li>
                                    ))}
                                  </ul>
                                )}
                            </div>
                          ) : (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={onQualify}
                              disabled={isQualifyPending || isPipelinePending}
                            >
                              {isQualifyPending || isPipelinePending ? (
                                <Loader2 className="mr-2 h-3 w-3 animate-spin" />
                              ) : null}
                              Qualify
                            </Button>
                          )}
                        </>
                      )}

                      {stageKey === 'analyzed' && (
                        <>
                          {company.hasAnalysis ? (
                            <div className="space-y-2">
                              <p className="text-sm text-muted-foreground">
                                Website analyzed
                              </p>
                              {company.profile_summary && (
                                <AnalyzedSummary
                                  summary={company.profile_summary}
                                />
                              )}
                              {(company.industry_tags?.length ||
                                company.offering_tags?.length ||
                                company.company_type_tags?.length) ? (
                                <div className="flex flex-wrap gap-1">
                                  {[
                                    ...(company.industry_tags ?? []),
                                    ...(company.offering_tags ?? []),
                                    ...(company.company_type_tags ?? []),
                                  ].map((tag) => (
                                    <Badge
                                      key={tag}
                                      variant="secondary"
                                      className="text-xs font-normal"
                                    >
                                      {tag}
                                    </Badge>
                                  ))}
                                </div>
                              ) : null}
                              {(company.profile_freshness?.last_profiled_at ||
                                company.website) && (
                                <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                                  {company.profile_freshness?.last_profiled_at && (
                                    <span>
                                      {company.profile_freshness.is_stale
                                        ? 'Outdated · '
                                        : ''}
                                      Last analyzed{' '}
                                      {new Date(
                                        company.profile_freshness.last_profiled_at
                                      ).toLocaleDateString()}
                                    </span>
                                  )}
                                  {company.website && (
                                    <a
                                      href={
                                        company.website.startsWith('http')
                                          ? company.website
                                          : `https://${company.website}`
                                      }
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="inline-flex items-center gap-1 text-primary hover:underline"
                                    >
                                      <ExternalLink className="h-3 w-3" />
                                      View website
                                    </a>
                                  )}
                                </div>
                              )}
                            </div>
                          ) : (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={onAnalyze}
                              disabled={isPipelinePending || isAnalyzePending}
                            >
                              {isPipelinePending || isAnalyzePending ? (
                                <Loader2 className="mr-2 h-3 w-3 animate-spin" />
                              ) : (
                                <Search className="mr-2 h-3 w-3" />
                              )}
                              Run Analysis
                            </Button>
                          )}
                        </>
                      )}

                      {stageKey === 'contact' && (
                        <ContactStepContent
                          company={company}
                          resolvedContact={resolvedContact}
                          hasContact={hasContact}
                          onFindContact={onFindContact}
                          isPipelinePending={isPipelinePending}
                        />
                      )}

                      {stageKey === 'outreach' && (
                        <>
                          {company.outreach ? (
                            <div className="flex flex-wrap items-center gap-2">
                              <p className="text-sm text-muted-foreground">
                                Outreach content generated
                              </p>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() =>
                                  navigate(`/companies/${company.id}/outreach`)
                                }
                              >
                                <ExternalLink className="mr-2 h-3 w-3" />
                                View Outreach
                              </Button>
                            </div>
                          ) : !hasEmail ? (
                            <p className="text-sm text-muted-foreground">
                              Requires contact email
                            </p>
                          ) : (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={onGenerateOutreach}
                              disabled={isPipelinePending}
                            >
                              {isPipelinePending ? (
                                <Loader2 className="mr-2 h-3 w-3 animate-spin" />
                              ) : (
                                <Mail className="mr-2 h-3 w-3" />
                              )}
                              Generate Outreach
                            </Button>
                          )}
                        </>
                      )}

                      {stageKey === 'reached' && company.outreach && (
                        <div className="space-y-2 text-sm">
                          {resolvedContact?.email && (
                            <div className="flex items-center gap-2">
                              <Mail className="h-4 w-4 shrink-0 text-muted-foreground" />
                              <span className="text-muted-foreground">
                                {emailSent ? (
                                  <>
                                    <CheckCircle2 className="mr-1 inline h-4 w-4 text-primary" />
                                    Email sent to {resolvedContact.email}
                                  </>
                                ) : (
                                  <>
                                    Ready to send to {resolvedContact.email}
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      className="ml-2"
                                      onClick={onSendEmail}
                                      disabled={isSendEmailPending}
                                    >
                                      {isSendEmailPending ? (
                                        <Loader2 className="h-3 w-3 animate-spin" />
                                      ) : (
                                        'Send Email'
                                      )}
                                    </Button>
                                  </>
                                )}
                              </span>
                            </div>
                          )}
                          {resolvedContact?.phone && (
                            <div className="flex items-center gap-2">
                              <Phone className="h-4 w-4 shrink-0 text-muted-foreground" />
                              <span className="text-muted-foreground">
                                {!company.calendar_ready_for_user ? (
                                  <>
                                    Calendar not connected
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      className="ml-2"
                                      onClick={() => navigate('/app/settings/account')}
                                    >
                                      Connect Calendar
                                    </Button>
                                  </>
                                ) : phoneCalled ? (
                                  <>
                                    <CheckCircle2 className="mr-1 inline h-4 w-4 text-primary" />
                                    Called {resolvedContact.phone}
                                  </>
                                ) : callInFlight ? (
                                  <>
                                    <Loader2 className="mr-1 inline h-3 w-3 animate-spin" />
                                    Call in progress
                                  </>
                                ) : (
                                  <>
                                    {resolvedContact.phone}
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      className="ml-2"
                                      onClick={onCall}
                                      disabled={isCallPending || !company.calendar_ready_for_user}
                                    >
                                      {isCallPending ? (
                                        <Loader2 className="h-3 w-3 animate-spin" />
                                      ) : (
                                        'Call'
                                      )}
                                    </Button>
                                  </>
                                )}
                              </span>
                            </div>
                          )}
                          {company.latest_call?.booking_status === 'booked' &&
                            company.latest_call.booked_start_at && (
                              <div className="text-muted-foreground">
                                <CheckCircle2 className="mr-1 inline h-4 w-4 text-primary" />
                                Meeting booked for{' '}
                                {new Date(company.latest_call.booked_start_at).toLocaleString()}
                              </div>
                            )}
                          {!resolvedContact?.email && !resolvedContact?.phone && (
                            <p className="text-muted-foreground">
                              No contact info to reach out
                            </p>
                          )}
                        </div>
                      )}
                      {stageKey === 'reached' && !company.outreach && (
                        <p className="text-sm text-muted-foreground">
                          Generate outreach first
                        </p>
                      )}
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      </CardContent>
    </Card>
  )
}
